import type {
  StaffAuthResultDto,
  StaffLoginInput,
  StaffMeDto,
  StaffSetupInput,
  StaffVerifyInput,
} from '@icebreaker/shared/staff';
import { isSessionRejection } from '@icebreaker/shared/errors';
import { api, getAccessToken, setAccessToken } from '@/shared/api/client';

/**
 * PERSONEL KİMLİĞİ — `api` katmanı, YALNIZCA sagalardan çağrılır (eslint
 * kuralı `no-api-in-ui` bileşenden çağrılmasını engeller).
 *
 * TÜM UÇLAR `/panel/auth/*` ALTINDADIR (panel kimliği ayrımı · Karar 3b).
 * Ürünün `/auth/*` uçlarına buradan TEK BİR ÇAĞRI DA gitmez; ölçüm
 * `admin-panel-endpoint.guard.spec.ts` içindedir (beklenen: 0).
 *
 * Giriş e-posta + ŞİFRE + tek kullanımlık koddur. Google/Apple sağlayıcıları
 * BİLİNÇLİ olarak yoktur ve panelde "şifremi unuttum" DA YOKTUR: kilitlenen
 * personeli başka bir yönetici yeniden davet eder (Karar 4c). Posta kutusuna
 * ek olarak bir İNSAN KARARI gerekir.
 */

/** Giriş 1. adım — OTURUM AÇMAZ; sunucu kodu gönderir. */
export function staffLogin(input: StaffLoginInput) {
  return api.staff.login(input);
}

/** Giriş 2. adım — oturumu açan tek uç. */
export async function staffVerify(input: StaffVerifyInput): Promise<StaffAuthResultDto> {
  const result = (await api.staff.verify(input)) as unknown as StaffAuthResultDto;
  setAccessToken(result.accessToken);
  return result;
}

/** İlk kurulum: davet jetonu tüketilir ve oturum AÇILIR (Karar 4c). */
export async function staffSetup(input: StaffSetupInput): Promise<StaffAuthResultDto> {
  const result = (await api.staff.setup(input)) as unknown as StaffAuthResultDto;
  setAccessToken(result.accessToken);
  return result;
}

/**
 * TEK-UÇUŞ KİLİDİ: aynı sekmede eşzamanlı sagalar 401 alıp aynı anda
 * yenilemeye kalkarsa hepsi TEK yenileme isteğini paylaşır.
 */
let refreshInFlight: Promise<{ accessToken: string }> | null = null;

function refreshOnce(): Promise<{ accessToken: string }> {
  if (!refreshInFlight) {
    refreshInFlight = (api.staff.refresh() as unknown as Promise<{ accessToken: string }>).finally(
      () => {
        refreshInFlight = null;
      },
    );
  }
  return refreshInFlight;
}

/**
 * RET BİR KEZ TEYİT EDİLİR. İki sekme aynı çerezi paylaşır; aynı anda
 * yenilerlerse sunucu birini döndürür, ötekine "rotasyon yarışı" 401'i
 * verir. İkinci deneme kazananın çerezini ya da tolerans penceresindeki
 * eski jetonu taşır ve geçer. Oturum gerçekten öldüyse ikincisi de
 * reddedilir. Yenileme en fazla İKİ kez gider, döngü yoktur.
 */
function refreshConfirmingRejection(confirm: boolean): Promise<{ accessToken: string }> {
  return refreshOnce().catch((error: unknown) => {
    if (!confirm || !isSessionRejection(error)) throw error;
    return refreshOnce();
  });
}

/**
 * Sayfa yenilendiğinde oturumu `wb_staff_refresh` çerezinden diriltir.
 *
 * GEÇİCİ HATA ≠ YETKİSİZLİK: yalnızca sunucunun kararı (401/403) oturumu
 * düşürür; 429/5xx/ağ hatası YUKARI FIRLATILIR ve "karar yok" olarak
 * işaretlenir — personel hız sınırına takıldı diye girişe atılmaz.
 *
 * İki sekme aynı anda yeniden yüklenince biri yarışı kaybeder (sıra 101):
 * ret burada da bir kez teyit edilir. Soğuk açılışta bellek jetonu yoktur,
 * çerez de okunamaz, bu yüzden teyit koşulsuzdur.
 */
export async function restoreSession(): Promise<{ me: StaffMeDto; accessToken: string } | null> {
  try {
    const pair = await refreshConfirmingRejection(true);
    setAccessToken(pair.accessToken);
    const me = (await api.staff.me()) as unknown as StaffMeDto;
    return { me, accessToken: pair.accessToken };
  } catch (error: unknown) {
    if (!isSessionRejection(error)) throw error;
    setAccessToken(null);
    return null;
  }
}

/**
 * 401 sonrası tek denemelik yenileme (jenerik `apiSaga` çağırır). Web'deki
 * desenin aynısı (sıra 45), panel küçük işler 1 · B-02 ile hizalandı.
 *
 * `false` OTURUMU KAPATIR: `apiSaga` `sessionRefreshRejected` yayar, auth
 * sagası depoyu sıfırlar ve kapı giriş ekranına götürür. Bu yüzden iki ayrım
 * şarttır:
 *  - GEÇİCİ HATA (429/5xx/ağ) `false` DEĞİLDİR: fırlatılır, istek yalnız hata
 *    olur, personel girişe atılmaz. Eskiden her hata `false` dönüyordu.
 *  - RET BİR KEZ TEYİT EDİLİR (`refreshConfirmingRejection`).
 */
export async function refreshForRetry(): Promise<boolean> {
  try {
    // Oturumsuz sekmede yarışacak oturum yoktur: teyit isteği gitmez.
    const pair = await refreshConfirmingRejection(getAccessToken() !== null);
    setAccessToken(pair.accessToken);
    return true;
  } catch (error: unknown) {
    if (!isSessionRejection(error)) throw error;
    setAccessToken(null);
    return false;
  }
}

export async function signOut(): Promise<void> {
  try {
    await api.staff.logout();
  } finally {
    // Sunucu isteği düşse de bellek jetonunu bırakırız; çerez sunucudadır.
    setAccessToken(null);
  }
}
